import React, { useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "../../Context/GlobalContext";
import { images } from "../../Assets/images";
import { apiEndPoints } from "../../Url";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Articles = () => {
  const {
    articlesList,
    getArticlesList,
    translatedLabels,
    pagination,
    setSearch,
    search,
  } = useContext(GlobalContext);
  const screenLabels = translatedLabels?.common;

  const currentArticles = articlesList;
  const { totalRecords, totalPages, currentPage, perPage } = pagination;
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    getArticlesList(currentPage, perPage);
  }, [currentPage, getArticlesList, perPage]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      getArticlesList(1);
    }, 500);

    return () => clearTimeout(searchDebounceRef.current);
  }, [search, getArticlesList]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        startPage = 2;
        endPage = Math.min(4, totalPages - 1);
      }

      if (currentPage >= totalPages - 2) {
        startPage = Math.max(totalPages - 3, 2);
        endPage = totalPages - 1;
      }

      if (startPage > 2) {
        pages.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      getArticlesList(page, perPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };
  return (
    <>
      <div className="content-part pt-5 px-0">
        <div
          style={{ position: "sticky", top: 0, zIndex: 1, background: "white" }}
          className=" w-100 pb-2 px-2"
        >
          <h5 className="py-2 mb-0">
            {screenLabels?.article_ai_wellness?.label_value ||
              "Articles on AI & Wellness"}
          </h5>

          <div>
            <input
              placeholder={
                screenLabels?.search_article?.label_value || "Search article"
              }
              type="text"
              className="form-control w-100"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="px-2">
          <div className="row p-0 m-0 mt-2">
            {currentArticles?.length > 0 ? (
              currentArticles.map((article) => (
                <div key={article.article_id} className="col-6 px-1">
                  <Link to={`/articles/${article.id || article?.article_id}`} className="card nav-link" style={{ height: "230px" }}>
                    <div className="article-img">
                      <img
                        src={
                          article?.image
                            ? apiEndPoints +
                            "/uploads/article_img/" +
                            article.image
                            : images.placeHolderImg
                        }
                        className="card-img-top "
                        alt={article.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = images.placeHolderImg;
                        }}
                      />
                    </div>

                    <div className="card-body d-flex flex-column">
                      <h6 className="card-title clamp-2 mb-1 fs-15px">
                        {article.name}
                      </h6>
                      <div className="mt-auto text-center">
                        <Link
                          to={`/articles/${article.id || article?.article_id}`}
                          className="btn btn-primary view-more-button"
                        >
                          {screenLabels?.view_more?.label_value || "View More"}
                        </Link>
                      </div>
                    </div>
                  </Link>
                </div>
              ))
            ) : (
              <div className="d-flex justify-content-center align-items-center w-100 mt-5">
                {screenLabels?.no_articles?.label_value || "No articles found"}
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <>
              <div className="d-flex justify-content-center">
                <nav aria-label="Articles pagination">
                  <div className="custom-pagination d-flex align-items-center">
                    <button
                      className="page-btn nav-btn"
                      onClick={handlePrevious}
                      disabled={currentPage === 1}
                      aria-label="Previous"
                    >
                      <span>
                        <ChevronLeft size={20} />
                      </span>
                    </button>

                    {getPageNumbers().map((page, index) => (
                      <button
                        key={index}
                        className={`page-btn ${page === currentPage ? "active" : ""
                          } ${page === "..." ? "dots" : ""}`}
                        onClick={() => page !== "..." && handlePageChange(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      className="page-btn nav-btn"
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      aria-label="Next"
                    >
                      <span>
                        <ChevronRight size={20} />
                      </span>
                    </button>
                  </div>
                </nav>
              </div>
            </>
          )}

          {totalPages > 1 && (
            <div className="text-center mt-2">
              <small className="text-muted">
                {screenLabels?.showing?.label_value || "Showing"}{" "}
                {(currentPage - 1) * perPage + 1}-
                {Math.min(currentPage * perPage, totalRecords)}{" "}
                {screenLabels?.of?.label_value || "of"} {totalRecords}{" "}
                {screenLabels?.articles?.label_value || "articles"}
              </small>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Articles;
